import React, { useEffect, useState } from 'react';
import { getSubdomain } from '../utils/subdomain';
import { organizations } from '../api';
import { OrgProvider, useOrg } from '../context/OrgContext';
import TekLoader from './TekLoader';

// Shown when the subdomain in the URL doesn't match any active organization.
const OrgNotFound = ({ subdomain }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
    <div className="text-center max-w-md px-8 py-12 bg-white rounded-3xl shadow-xl border border-slate-100">
      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <span className="text-3xl">🏢</span>
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Workspace Not Found</h1>
      <p className="text-slate-500 font-medium mb-1">
        No organization exists for:
      </p>
      <p className="text-mint-700 font-bold mb-6 break-all">{window.location.host}</p>
      <p className="text-sm text-slate-400">
        Check the URL or contact your administrator.
      </p>
    </div>
  </div>
);

/**
 * SubdomainGuard — wraps the entire app.
 *
 * - No subdomain in URL (plain localhost / root domain) → render children as-is.
 * - Subdomain present → validate it against the backend before rendering.
 *   - Valid org → render children.
 *   - Unknown subdomain → show "Workspace Not Found" screen.
 */
const SubdomainGuardInner = ({ children }) => {
  const subdomain = getSubdomain();
  const { setOrgName } = useOrg();
  // 'idle' | 'checking' | 'valid' | 'invalid'
  const [status, setStatus] = useState(subdomain ? 'checking' : 'valid');

  useEffect(() => {
    if (!subdomain) return;

    organizations
      .validate(subdomain)
      .then((res) => {
        if (res.data?.name) {
          document.title = res.data.name;
          setOrgName(res.data.name);
        }
        setStatus('valid');
      })
      .catch(() => setStatus('invalid'));
  }, [subdomain, setOrgName]);

  if (status === 'checking') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--paper)' }}
      >
        <TekLoader size={22} label="Verifying workspace…" />
      </div>
    );
  }

  if (status === 'invalid') {
    return <OrgNotFound subdomain={subdomain} />;
  }

  return children;
};

const SubdomainGuard = ({ children }) => (
  <OrgProvider>
    <SubdomainGuardInner>{children}</SubdomainGuardInner>
  </OrgProvider>
);

export default SubdomainGuard;
